<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldObservation } from "@/types/field-observations.types";
import {
  getObservationTitle,
  getObservationTypeConfig,
} from "@/components/field-observations/observation-type";
import { useObservationsList } from "@/composable/useObservationsList";
import { useDates } from "@/composable/useDates";
import ObservationCardEditor from "./ObservationCardEditor.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import APIClient from "@/APIClient";

/**
 * Carte d'une observation dans le volet Observations — même système ouvert /
 * fermé que la carte d'un groupe d'analyse, en bleu. Fermée : sa lettre (la
 * même que sur son cadre du plan), son type, son nom ou à défaut son type,
 * son commentaire tronqué, et les raccourcis photos / vocal. L'ouvrir (une
 * seule à la fois) cadre le plan sur elle et déplie tout le formulaire, en
 * place dans la liste (`ObservationCardEditor`). La carte se surligne quand
 * une de ses entités est sélectionnée sur le plan.
 */

const props = defineProps<{
  observation: FieldObservation;
}>();

const { t } = useTranslation();
const { formatDistanceToNow } = useDates();
const list = useObservationsList();

const isOpen = computed(
  () => list.openObservationId.value === props.observation.id,
);

const letter = computed(
  () => list.letters.value.get(props.observation.id) ?? "",
);

/** Entités du plan rattachées à cette observation. */
const entities = computed(() =>
  props.observation.localId
    ? (list.entitiesByObservation.value.get(props.observation.localId) ?? [])
    : [],
);

/** Au moins une entité rattachée est dans la sélection courante du plan. */
const highlighted = computed(
  () =>
    !!props.observation.localId &&
    list.selectedObservationIds.value.includes(props.observation.localId),
);

const typeConfig = computed(() =>
  getObservationTypeConfig(props.observation.type),
);

/** Titre : le nom de l'observation, à défaut son type. */
const title = computed(() => getObservationTitle(props.observation, t));

const photos = computed(() => props.observation.photos ?? []);
const audios = computed(() => props.observation.audioRecordings ?? []);

const handleCardClick = (): void => {
  if (!isOpen.value) list.open(props.observation);
};

// La carte ouverte vient se placer dans la partie visible de la liste — quand
// elle s'ouvre, mais aussi quand elle est MONTÉE déjà ouverte : observation
// tout juste créée, ou carte déplacée dans une autre section parce que sa
// partie vient de changer (vues par partie / arborescence).
const cardEl = ref<HTMLElement | null>(null);
const revealIfOpen = async (): Promise<void> => {
  if (!isOpen.value) return;
  await nextTick();
  cardEl.value?.scrollIntoView({ block: "nearest", behavior: "smooth" });
};
watch(isOpen, revealIfOpen);
onMounted(revealIfOpen);
// Dans les vues par partie, la carte est déplacée (pas remontée) quand sa
// partie change ; et un badge touché sur le plan redemande à la voir.
watch(
  [() => props.observation.partId, () => list.revealToken.value],
  revealIfOpen,
);

// ── Visionneuse photos (URLs chargées au clic) ──────────────────────────────
const showViewer = ref(false);
const viewerUrls = ref<string[]>([]);
const isLoadingPhotos = ref(false);

// La visionneuse est téléportée dans <body> et sa racine <transition> ne
// propage pas la classe → on marque <body> pendant l'ouverture pour assombrir
// le masque (sinon l'outil dessin transparaît derrière).
const BODY_VIEWER_CLASS = "observation-photo-viewer-open";
watch(showViewer, (open) => {
  document.body.classList.toggle(BODY_VIEWER_CLASS, open);
});

const openPhotos = async (): Promise<void> => {
  if (isLoadingPhotos.value || !photos.value.length) return;
  isLoadingPhotos.value = true;
  try {
    viewerUrls.value = await Promise.all(
      photos.value.map(async (p) => (await APIClient.getFileUrl(p.fileId)).url),
    );
    showViewer.value = true;
  } catch (e) {
    console.error("Failed to load photo URLs:", e);
  } finally {
    isLoadingPhotos.value = false;
  }
};

// ── Lecture vocal (premier enregistrement, toggle) ──────────────────────────
const isPlaying = ref(false);
const isLoadingAudio = ref(false);
let audioEl: HTMLAudioElement | null = null;

const stopAudio = (): void => {
  audioEl?.pause();
  audioEl = null;
  isPlaying.value = false;
};

const toggleAudio = async (): Promise<void> => {
  if (isPlaying.value) {
    stopAudio();
    return;
  }
  const first = audios.value[0];
  if (!first || isLoadingAudio.value) return;
  isLoadingAudio.value = true;
  try {
    const { url } = await APIClient.getFileUrl(first.fileId);
    audioEl = new Audio(url);
    audioEl.onended = () => {
      isPlaying.value = false;
      audioEl = null;
    };
    await audioEl.play();
    isPlaying.value = true;
  } catch (e) {
    console.error("Failed to play audio:", e);
  } finally {
    isLoadingAudio.value = false;
  }
};

// Le raccourci de lecture disparaît à l'ouverture (les vocaux sont dans le
// formulaire) : on ne laisse pas un son tourner sans bouton pour l'arrêter.
watch(isOpen, (open) => {
  if (open) stopAudio();
});

onUnmounted(() => {
  stopAudio();
  document.body.classList.remove(BODY_VIEWER_CLASS);
});
</script>

<template>
  <div
    ref="cardEl"
    class="observation-card"
    :class="{ open: isOpen, highlighted }"
    role="button"
    @click="handleCardClick"
  >
    <!-- En-tête : lettre (celle du cadre sur le plan), type, titre, date -->
    <div class="d-flex align-items-center gap-2">
      <span class="card-letter flex-shrink-0">{{ letter }}</span>
      <PhosphorIcon
        :name="typeConfig.icon"
        :size="14"
        weight="bold"
        class="type-icon flex-shrink-0"
      />
      <span class="fw-medium text-truncate flex-grow-1">{{ title }}</span>
      <!-- Carte ouverte : la date cède la place au titre et au repli -->
      <span v-if="!isOpen" class="created-at flex-shrink-0">
        {{ formatDistanceToNow(observation.createdAt) }}
      </span>
      <button
        v-else
        type="button"
        class="collapse-btn flex-shrink-0"
        :aria-label="t('close')"
        @click.stop="list.close()"
      >
        <PhosphorIcon name="caret-up" :size="14" weight="bold" />
      </button>
    </div>

    <!-- Carte fermée : commentaire tronqué + raccourcis -->
    <template v-if="!isOpen">
      <div v-if="observation.description" class="card-finding mt-1">
        {{ observation.description }}
      </div>

      <div
        v-if="photos.length || audios.length"
        class="d-flex align-items-center flex-wrap gap-1 mt-2"
      >
        <button
          v-if="photos.length"
          type="button"
          class="media-btn"
          :disabled="isLoadingPhotos"
          @click.stop="openPhotos"
        >
          <span v-if="isLoadingPhotos" class="spinner-border spinner-border-sm" />
          <PhosphorIcon v-else name="image" :size="15" weight="bold" />
          {{
            t("canvas:observations.item.photosCount", { count: photos.length })
          }}
        </button>

        <button
          v-if="audios.length"
          type="button"
          class="media-btn"
          :disabled="isLoadingAudio"
          @click.stop="toggleAudio"
        >
          <span v-if="isLoadingAudio" class="spinner-border spinner-border-sm" />
          <PhosphorIcon
            v-else
            :name="isPlaying ? 'stop' : 'play'"
            :size="15"
            weight="bold"
          />
          {{
            isPlaying
              ? t("canvas:observations.item.stop")
              : t("canvas:observations.item.listen")
          }}
        </button>
      </div>
    </template>

    <!-- Carte ouverte : le formulaire, en place dans la liste.
         data-bs-theme="dark" re-résout les variables Bootstrap du sous-arbre :
         les composants observations partagés (sélecteur de partie, lecteur
         audio…) passent en sombre sans styles dédiés. -->
    <div v-else data-bs-theme="dark" class="card-editor mt-3">
      <ObservationCardEditor :observation="observation" :entities="entities" />
    </div>

    <!-- Visionneuse photos -->
    <el-image-viewer
      v-if="showViewer"
      :url-list="viewerUrls"
      hide-on-click-modal
      teleported
      @close="showViewer = false"
    />
  </div>
</template>

<style lang="scss" scoped>
.observation-card {
  // Couleur d'accent des champs, de la liste d'entités et de la ligne
  // d'ajout : le bleu des observations (violet = analyse).
  --card-accent: var(--bs-primary);
  --card-accent-light: color-mix(in srgb, var(--bs-primary) 65%, white);

  border: 1px solid var(--canvas-border);
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  background-color: var(--canvas-bg);
  // Amenée à l'écran, la carte s'arrête SOUS la barre d'outils collante de
  // la liste (filtre / tri), pas dessous-derrière.
  scroll-margin-top: 52px;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover:not(.open) {
    background-color: rgba(255, 255, 255, 0.05);
  }

  // Une de ses entités est sélectionnée sur le plan.
  &.highlighted {
    border-color: var(--bs-primary);
    background-color: rgba(255, 255, 255, 0.06);
  }

  // Carte ouverte : fond légèrement teinté de la couleur de l'onglet.
  &.open {
    cursor: default;
    border-color: var(--bs-primary);
    box-shadow: 0 0 0 1px var(--bs-primary);
    background-color: color-mix(
      in srgb,
      var(--bs-primary) 16%,
      var(--canvas-bg)
    );
  }
}

// Icône du type, toujours dans la couleur de l'onglet (bleu = observations).
.type-icon {
  color: var(--card-accent-light);
}

.card-letter {
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 10px;
  background-color: var(--bs-primary);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.created-at {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
}

.collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  opacity: 0.7;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    opacity: 1;
  }
}

.card-finding {
  font-size: 0.78rem;
  opacity: 0.7;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.media-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: var(--bs-border-radius);
  background-color: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem;
  padding: 4px 9px;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.15);
    color: white;
  }

  .spinner-border-sm {
    width: 12px;
    height: 12px;
  }
}

.card-editor {
  color: var(--bs-body-color);
}
</style>

<style lang="scss">
// Non-scopé : la visionneuse est téléportée dans <body> (classe posée sur le
// body à l'ouverture). Fond quasi opaque — le masque par défaut (noir 50 %)
// laisse transparaître l'outil dessin.
body.observation-photo-viewer-open .el-image-viewer__mask {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.94);
}
</style>
